import { useState } from "react";
import type { TocItem } from "../html";

interface Props {
  toc: TocItem[];
  activeId: string | null;
  onNavigate: (id: string) => void;
}

/** Sidebar table of contents: sections, with the active section's subsections expanded. */
export function TocSidebar({ toc, activeId, onNavigate }: Props) {
  // Sections the reader expanded by hand, in addition to the active one.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const activeSection = toc.find((s) => s.id === activeId || s.children.some((c) => c.id === activeId))?.id;

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!toc.length) return <nav className="toc" aria-label="Contents" />;

  return (
    <nav className="toc" aria-label="Contents">
      <div className="toc-title">Contents</div>
      <ol className="toc-list">
        {toc.map((section) => {
          const open = section.id === activeSection || expanded.has(section.id);
          return (
            <li key={section.id} className={section.id === activeSection ? "toc-section active" : "toc-section"}>
              <div className="toc-row">
                <a
                  href={`#${section.id}`}
                  className={activeId === section.id ? "toc-link current" : "toc-link"}
                  aria-current={activeId === section.id ? "location" : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(section.id);
                  }}
                >
                  {section.text}
                </a>
                {section.children.length > 0 && (
                  <button
                    className="toc-toggle"
                    aria-label={open ? `Collapse ${section.text}` : `Expand ${section.text}`}
                    aria-expanded={open}
                    onClick={() => toggle(section.id)}
                  >
                    {open ? "−" : "+"}
                  </button>
                )}
              </div>
              {open && section.children.length > 0 && (
                <ol className="toc-sublist">
                  {section.children.map((sub) => (
                    <li key={sub.id}>
                      <a
                        href={`#${sub.id}`}
                        className={activeId === sub.id ? "toc-link sub current" : "toc-link sub"}
                        aria-current={activeId === sub.id ? "location" : undefined}
                        onClick={(e) => {
                          e.preventDefault();
                          onNavigate(sub.id);
                        }}
                      >
                        {sub.text}
                      </a>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
